import { useEffect } from 'react'
import { useBrand } from './brandContext'

const FAVICON_SELECTOR = 'link[rel="icon"]'
const BRAND_FONT_ID = 'brand-font'

/**
 * Applies the organization brand to the document shell — favicon and the
 * fallback tab title.
 *
 * One build serves every organization, so `index.html` ships the default
 * brand's icon and title; this swaps them once the resolved brand is known.
 */
export const useBrandDocument = (): void => {
  const brand = useBrand()

  useEffect(() => {
    let link = document.head.querySelector<HTMLLinkElement>(FAVICON_SELECTOR)

    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }

    link.href = brand.favicon
    link.type = brand.favicon.endsWith('.svg') ? 'image/svg+xml' : 'image/png'

    // The brand's typeface. index.html ships the platform font; a brand that
    // specifies its own (Lowcountry Male → Open Sans) loads it here, since one
    // build serves every organization.
    const existing = document.getElementById(BRAND_FONT_ID)
    existing?.remove()

    if (brand.typography?.googleFont) {
      const font = document.createElement('link')
      font.id = BRAND_FONT_ID
      font.rel = 'stylesheet'
      font.href = `https://fonts.googleapis.com/css2?family=${brand.typography.googleFont}&display=swap`
      document.head.appendChild(font)
    }

    // Pages set their own <title> via Helmet/usePageTitle; this is the value
    // shown before the first page mounts and after it unmounts.
    document.title = brand.name
  }, [brand])
}
